import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert, WhatsAppPreview } from '@repletoryreach/sdk-ui/data-display';
import { Select, TokenInput, TokenTextarea } from '@repletoryreach/sdk-ui/inputs';
import type { templates as templatesApi } from '@repletoryreach/sdk-api';
import { useT } from '../../../../i18n/I18nProvider';
import { useAttributeSuggestions } from '../../../../lib/attributes';
import { parseComponents, extractVariables, type ParsedComponent } from '../../../../lib/template-components';

/** The longest a free-form WhatsApp text body may be. */
export const BODY_MAX = 4096;

export interface ComposeMessageProps {
  /** `text` while every recipient is inside the window; `template` otherwise. */
  mode: 'text' | 'template';
  body: string;
  onBody: (next: string) => void;
  templateCode: string;
  onTemplateCode: (next: string) => void;
  variables: Record<string, string>;
  onVariables: (next: Record<string, string>) => void;
  sendable: templatesApi.MessageTemplate[];
  templatesLoading: boolean;
  /** Approved templates hidden because they were auto-approved outside production. */
  blockedByDevFallback: number;
  errors: Record<string, string>;
  disabled?: boolean;
}

/**
 * What the message says: free text inside the window, an approved template
 * outside it.
 *
 * <p>Deliberately a sibling of the broadcast wizard's `MessageStep` rather
 * than a shared component. That one is welded to `BroadcastDraftState` through
 * its `state`/`set` props and speaks in `broadcasts.*` copy. The genuinely
 * shared parts are shared: `WhatsAppPreview` from the design system,
 * `parseComponents` / `extractVariables` from `lib/template-components`, and
 * the attribute picker (`TokenInput`, `useAttributeSuggestions`), which is
 * where the wizard gets them too.</p>
 *
 * <p>Compose sends one message per recipient, and since 10 September 2026 the
 * inbox resolves `{{contact.name}}`, `{{contact.fields.<key>}}` and the
 * `|fallback` grammar per person before each send
 * (`services/inbox/Services/Personalisation.cs`, the same rule the
 * broadcasts service applies). So the body and every template value take
 * `{{` here, and each person receives their own name. Before that date the
 * text went out literally, which is why the copy used to say a blank was one
 * value for all of them.</p>
 */
export function ComposeMessage({
  mode, body, onBody, templateCode, onTemplateCode, variables, onVariables,
  sendable, templatesLoading, blockedByDevFallback, errors, disabled
}: ComposeMessageProps) {
  const t = useT();
  const suggestions = useAttributeSuggestions();
  const fallbackHint = t('attributes.fallbackHint');

  const template = useMemo(
    () => sendable.find((tpl) => tpl.code === templateCode) ?? null,
    [sendable, templateCode]
  );
  const parsed = useMemo(() => parseComponents(template?.components), [template?.components]);
  const vars = useMemo(() => extractVariables(parsed).map(String), [parsed]);

  const header = parsed.find((c) => c.type === 'HEADER') ?? null;
  const bodyPart = parsed.find((c) => c.type === 'BODY') ?? null;
  const footer = parsed.find((c) => c.type === 'FOOTER') ?? null;
  const buttons = parsed.find((c) => c.type === 'BUTTONS')?.buttons ?? [];

  if (mode === 'text') {
    return (
      <Stack gap="md" className="rr-compose__compose">
        <TokenTextarea
          label={t('compose.message.label')}
          description={t('compose.message.hint')}
          placeholder={t('compose.message.placeholder')}
          error={errors.body}
          value={body}
          disabled={disabled}
          minRows={5}
          maxRows={14}
          maxLength={BODY_MAX}
          tokens={suggestions}
          fallbackHint={fallbackHint}
          onChange={(e) => onBody(e.currentTarget.value)}
        />
        <Text role="caption" c="var(--rr-color-text-muted)">
          {t('compose.message.count', { length: body.length, max: BODY_MAX })}
        </Text>
        <div className="rr-compose__preview">
          <WhatsAppPreview
            chrome
            body={body}
            emptyBodyLabel={t('compose.message.previewEmpty')}
            businessName={t('compose.message.previewBusiness')}
          />
        </div>
      </Stack>
    );
  }

  return (
    <Stack gap="md" className="rr-compose__compose">
      <Stack gap="sm">
        <Select
          label={t('compose.template.label')}
          description={t('compose.template.hint')}
          error={errors.templateCode}
          value={templateCode}
          disabled={disabled}
          // A different template has different placeholders, so carrying the
          // old values across would land them on the wrong positions.
          onChange={(e) => { onTemplateCode(e.currentTarget.value); onVariables({}); }}
          data={[
            { value: '', label: t('common.select') },
            ...sendable.map((tpl) => ({ value: tpl.code, label: `${tpl.code} (${tpl.language})` }))
          ]}
        />

        {sendable.length === 0 && !templatesLoading && (
          <Alert color="info" title={t('compose.template.noneApproved.title')}>
            <Stack gap={6}>
              <Text role="bodySmall">{t('compose.template.noneApproved.body')}</Text>
              <Link to="/templates" className="rr-compose__link">
                {t('compose.template.noneApproved.action')}
              </Link>
            </Stack>
          </Alert>
        )}

        {blockedByDevFallback > 0 && (
          <Alert color="warning">{t('compose.template.devFallbackHidden', { count: blockedByDevFallback })}</Alert>
        )}

        {template && vars.length > 0 && (
          <Stack gap="sm">
            <Text role="inputLabel">{t('compose.template.placeholders')}</Text>
            {/* A blank may be the same words for everyone, or a field from
                each person's contact card; the hint says how to pick one. */}
            <Text role="caption" c="var(--rr-color-text-muted)">
              {t('compose.template.placeholders.hint')}
            </Text>
            {vars.map((position) => (
              <TokenInput
                key={position}
                label={t('compose.template.placeholder', { n: position })}
                error={errors[`var-${position}`]}
                value={variables[position] ?? ''}
                disabled={disabled}
                tokens={suggestions}
                fallbackHint={fallbackHint}
                onChange={(e) => onVariables({ ...variables, [position]: e.currentTarget.value })}
              />
            ))}
          </Stack>
        )}

        {template && vars.length === 0 && (
          <Text role="bodySmall" c="var(--rr-color-text-muted)">
            {t('compose.template.noPlaceholders')}
          </Text>
        )}
      </Stack>

      {template && (
        <div className="rr-compose__preview">
          <WhatsAppPreview
            chrome
            header={header ? { text: header.text, format: header.format } : null}
            body={fill(bodyPart?.text ?? '', variables)}
            footer={footer?.text ? fill(footer.text, variables) : null}
            buttons={buttons.map((b) => ({ ...b, text: fill(b.text, variables) }))}
            emptyBodyLabel={t('compose.message.previewEmpty')}
            businessName={t('compose.message.previewBusiness')}
            renderPlaceholder={(pos) => <em className="rr-compose__placeholder">{`{{${pos}}}`}</em>}
          />
        </div>
      )}
    </Stack>
  );
}

/** Substitutes what has been typed into `{{n}}`, leaving unfilled ones alone. */
function fill(text: string, values: Record<string, string>): string {
  return text.replace(/\{\{\s*(\d+)\s*\}\}/g, (whole, index: string) => values[index]?.trim() || whole);
}

/**
 * The template's placeholder values in the shape channel-wa sends to Meta.
 * Exported because the page builds the send payload, not this component.
 * A value may carry a contact token; the inbox resolves it per recipient.
 */
export function templateComponents(
  parsed: ParsedComponent[],
  variables: Record<string, string>
): { type: string; parameters: { type: string; text: string }[] }[] {
  const out: { type: string; parameters: { type: string; text: string }[] }[] = [];
  for (const part of parsed) {
    // Only the parts that actually carry a placeholder are sent: Meta refuses
    // a component with an empty parameter list.
    const positions = extractVariables([part]).map(String);
    if (positions.length === 0) continue;
    out.push({
      type: part.type.toLowerCase(),
      parameters: positions.map((p) => ({ type: 'text', text: variables[p] ?? '' }))
    });
  }
  return out;
}
